import { fabric } from 'fabric';
import { CardFaceData } from '../../global-types';

/** Custom properties that must survive the toObject() → enlivenObjects round-trip. */
const customProperties = ['data', 'name', 'btnId'];

/**
 * Wraps `fabric.util.enlivenObjects` in a Promise for async/await usage.
 */
const enlivenObjects = (serializedObjects: object[]): Promise<fabric.Object[]> =>
  new Promise((resolve) => {
    if (!serializedObjects.length) {
      resolve([]);
      return;
    }
    fabric.util.enlivenObjects(
      serializedObjects,
      (enlivenedObjects: fabric.Object[]) => {
        resolve(enlivenedObjects);
      },
      'fabric',
    );
  });

/**
 * Serializes and enlivens objects from a canvas that pass the position filter,
 * then adds them to the target canvas.
 */
const cloneFilteredObjects = async (
  sourceCanvas: fabric.Canvas,
  targetCanvas: fabric.StaticCanvas,
  filter: (obj: fabric.Object) => boolean,
): Promise<void> => {
  const serialized = sourceCanvas
    .getObjects()
    .filter(filter)
    .map((obj: fabric.Object) => {
      // The edit-history serialization projection exposes already-serialized plain
      // objects (no toObject); real fabric canvases expose toObject().
      const serializable = obj as Partial<fabric.Object>;
      return typeof serializable.toObject === 'function' ? serializable.toObject(customProperties) : obj;
    });
  const enlivened = await enlivenObjects(serialized);
  enlivened.forEach((obj) => targetCanvas.add(obj));
};

/**
 * Merges an inside-left / inside-right pair into a single synthesized CardFaceData
 * backed by an off-screen StaticCanvas sized to the full logical dimensions.
 */
const mergeInsidePanels = async (left: CardFaceData, right: CardFaceData): Promise<CardFaceData> => {
  /** Identity matrix — no pan or zoom applied to the merged canvas. */
  const identityTransform: [number, number, number, number, number, number] = [1, 0, 0, 1, 0, 0];

  const halfPanelWidth = left.dimensions.width;
  const fullWidth = halfPanelWidth * 2;
  const fullHeight = left.dimensions.height;

  // Each half-panel canvas may contain all objects (from loadFromJSON).
  // The midpoint divides objects into left-half vs right-half by position.
  const panelMidpoint = halfPanelWidth / 2;

  const staticCanvas = new fabric.StaticCanvas(null, { width: fullWidth, height: fullHeight });
  staticCanvas.setViewportTransform(identityTransform);

  const leftCanvas = left.canvas?.current;
  const rightCanvas = right.canvas?.current;

  if (leftCanvas) {
    const bgImage = leftCanvas.backgroundImage;
    if (bgImage) {
      staticCanvas.backgroundImage = bgImage;
    }

    await cloneFilteredObjects(leftCanvas, staticCanvas, (obj) => (obj.left ?? 0) < panelMidpoint);
  }

  if (rightCanvas) {
    await cloneFilteredObjects(rightCanvas, staticCanvas, (obj) => (obj.left ?? 0) >= panelMidpoint);
  }

  staticCanvas.requestRenderAll();

  return {
    ...left,
    type: 'inside',
    dimensions: { width: fullWidth, height: fullHeight },
    canvas: { current: staticCanvas } as unknown as CardFaceData['canvas'],
  };
};

/**
 * Recombines an `inside-left` / `inside-right` pair in the card faces list into
 * a single synthesized `inside` entry backed by an off-screen `fabric.StaticCanvas`
 * sized to the full logical width and height.
 *
 * All other face types are returned unchanged. Safe to call when the split-inside-panel
 * feature flag is off (no `inside-left` entries → pure pass-through).
 */
export const collapseSplitPanelFaces = async (cardFacesList: CardFaceData[]): Promise<CardFaceData[]> => {
  const hasSplitFace = cardFacesList.some((face) => face.type === 'inside-left');
  if (!hasSplitFace) {
    return cardFacesList;
  }

  const result: CardFaceData[] = [];
  let i = 0;
  while (i < cardFacesList.length) {
    // .at() avoids object-injection-sink lint rule while remaining readable
    const current = cardFacesList[`${i}`];
    const next = cardFacesList[`${i + 1}`];

    if (current.type === 'inside-left' && next?.type === 'inside-right' && current.faceId === next.faceId) {
      result.push(await mergeInsidePanels(current, next));
      i += 2;
    } else {
      result.push(current);
      i += 1;
    }
  }

  return result;
};
